import { useState, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface ShippingMethod {
  readonly id: string;
  readonly label: string;
  readonly defaultPrice: number; // agorot
  readonly needsAddress: boolean;
}

const SHIPPING_METHODS: readonly ShippingMethod[] = [
  { id: "israel_post", label: "\u05D3\u05D5\u05D0\u05E8 \u05D9\u05E9\u05E8\u05D0\u05DC", defaultPrice: 2500, needsAddress: false },
  { id: "hfd", label: "HFD \u05DE\u05E9\u05DC\u05D5\u05D7\u05D9\u05DD", defaultPrice: 3500, needsAddress: false },
  { id: "boxit", label: "Boxit \u05DC\u05D5\u05E7\u05E8\u05D9\u05DD", defaultPrice: 2000, needsAddress: false },
  { id: "self_pickup", label: "\u05D0\u05D9\u05E1\u05D5\u05E3 \u05E2\u05E6\u05DE\u05D9", defaultPrice: 0, needsAddress: true },
];

interface SelectedMethod {
  readonly id: string;
  readonly price: number;
  readonly address?: string;
}

interface ShippingConfigStepProps {
  readonly businessAddress: string;
  readonly onComplete: (methods: readonly SelectedMethod[]) => void;
}

function formatNIS(agorot: number): string {
  return `\u20AA${(agorot / 100).toFixed(0)}`;
}

export function ShippingConfigStep({ businessAddress, onComplete }: ShippingConfigStepProps): ReactNode {
  const [selected, setSelected] = useState<Record<string, { price: number; address: string }>>({});
  const [error, setError] = useState<string | null>(null);

  const toggleMethod = useCallback((method: ShippingMethod) => {
    setSelected((prev) => {
      if (prev[method.id] !== undefined) {
        const next = { ...prev };
        delete next[method.id];
        return next;
      }
      return {
        ...prev,
        [method.id]: {
          price: method.defaultPrice,
          address: method.needsAddress ? businessAddress : "",
        },
      };
    });
    setError(null);
  }, [businessAddress]);

  const updatePrice = useCallback((id: string, price: string) => {
    setSelected((prev) => {
      const existing = prev[id];
      if (existing === undefined) return prev;
      return { ...prev, [id]: { ...existing, price: Math.round(Number(price) * 100) || 0 } };
    });
  }, []);

  const updateAddress = useCallback((id: string, address: string) => {
    setSelected((prev) => {
      const existing = prev[id];
      if (existing === undefined) return prev;
      return { ...prev, [id]: { ...existing, address } };
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const entries = Object.entries(selected);
    if (entries.length === 0) {
      setError("\u05D1\u05D7\u05E8\u05D5 \u05DC\u05E4\u05D7\u05D5\u05EA \u05D0\u05E4\u05E9\u05E8\u05D5\u05EA \u05DE\u05E9\u05DC\u05D5\u05D7 \u05D0\u05D7\u05EA");
      return;
    }
    const methods: SelectedMethod[] = entries.map(([id, data]) => ({
      id,
      price: data.price,
      ...(data.address ? { address: data.address } : {}),
    }));
    onComplete(methods);
  }, [selected, onComplete]);

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05DE\u05E9\u05DC\u05D5\u05D7"}</h2>
      <p className="text-sm text-muted-foreground">
        {"\u05D1\u05D7\u05E8\u05D5 \u05D0\u05EA \u05D0\u05E4\u05E9\u05E8\u05D5\u05D9\u05D5\u05EA \u05D4\u05DE\u05E9\u05DC\u05D5\u05D7 \u05E9\u05DC\u05DB\u05DD. \u05E0\u05D9\u05EA\u05DF \u05DC\u05D4\u05EA\u05D0\u05D9\u05DD \u05D0\u05EA \u05D4\u05DE\u05D7\u05D9\u05E8\u05D9\u05DD."}
      </p>

      <div className="flex flex-col gap-3">
        {SHIPPING_METHODS.map((method) => {
          const isSelected = selected[method.id] !== undefined;
          const data = selected[method.id];
          return (
            <Card key={method.id} className={isSelected ? "border-primary" : ""}>
              <CardContent className="flex flex-col gap-3 p-4">
                <button
                  type="button"
                  className="flex min-h-[44px] w-full items-center gap-3 text-start"
                  onClick={() => toggleMethod(method)}
                >
                  <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                    isSelected ? "border-primary bg-primary" : "border-muted-foreground/40"
                  }`}>
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </div>
                  <div className="flex flex-1 items-center justify-between">
                    <span className="font-medium">{method.label}</span>
                    <span className="text-sm text-muted-foreground">
                      {method.defaultPrice === 0 ? "\u05D7\u05D9\u05E0\u05DD" : formatNIS(method.defaultPrice)}
                    </span>
                  </div>
                </button>

                {isSelected && data !== undefined && (
                  <div className="flex flex-col gap-2 ps-8">
                    <div className="flex items-center gap-2">
                      <label className="text-sm">{"\u05DE\u05D7\u05D9\u05E8 (\u20AA):"}</label>
                      <Input
                        type="number"
                        dir="ltr"
                        min="0"
                        step="1"
                        value={(data.price / 100).toString()}
                        onChange={(e) => updatePrice(method.id, e.target.value)}
                        className="min-h-[44px] w-24 text-base"
                      />
                    </div>
                    {method.needsAddress && (
                      <Input
                        value={data.address}
                        onChange={(e) => updateAddress(method.id, e.target.value)}
                        placeholder={"\u05DB\u05EA\u05D5\u05D1\u05EA \u05DC\u05D0\u05D9\u05E1\u05D5\u05E3"}
                        className="min-h-[44px] text-base"
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      <Button size="lg" className="min-h-[44px]" onClick={handleSubmit}>
        {"\u05D4\u05DE\u05E9\u05DA"}
      </Button>
    </div>
  );
}
